import { useCallback, useEffect, useRef, useState } from 'react';
import { EditorStore } from '../editor/store';
import { createPattern } from '../domain/pattern';
import { LIBRARIES } from '../domain/palettes';
import { EMPTY } from '../domain/types';
import { parsePatternFile } from '../io/format';
import { StorageFullError, loadPrefs, loadProject, saveProject, savePrefs } from '../io/storage';
import { CanvasView } from './CanvasView';
import { LeftPanel } from './LeftPanel';
import { RightPanel } from './RightPanel';
import {
  AboutDialog,
  ExportDialog,
  ImageImportDialog,
  NewPatternDialog,
  ProjectsDialog,
  ShortcutsDialog,
} from './dialogs';
import { COMPACT_QUERY, Menu, PHONE_QUERY, StoreContext, pickFile, useEditor, useMediaQuery } from './kit';
import type { MenuItem } from './kit';

function starterPattern() {
  const delica = LIBRARIES[0].colors;
  const p = createPattern({
    name: 'Untitled pattern',
    stitch: 'loom',
    width: 31,
    height: 40,
    palette: [delica[0], delica[2], delica[36], delica[57]].filter(Boolean),
  });
  return p;
}

export function App() {
  const [store] = useState(() => {
    const prefs = loadPrefs();
    const last = prefs.lastProjectId ? loadProject(prefs.lastProjectId) : null;
    return new EditorStore(last ?? starterPattern());
  });
  return (
    <StoreContext.Provider value={store}>
      <Shell />
    </StoreContext.Provider>
  );
}

type DialogId = 'new' | 'projects' | 'export' | 'shortcuts' | 'about' | null;

function Shell() {
  const { store, state } = useEditor();
  const [dialog, setDialog] = useState<DialogId>(null);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [theme, setTheme] = useState<'dark' | 'light'>(() => loadPrefs().theme);
  const [dropActive, setDropActive] = useState(false);
  const compact = useMediaQuery(COMPACT_QUERY);
  const phone = useMediaQuery(PHONE_QUERY);
  const [leftOpen, setLeftOpen] = useState(false);
  const [rightOpen, setRightOpen] = useState(false);

  // Leaving compact re-docks both panels, so returning to a wide window never
  // leaves a drawer stuck open over the chart.
  useEffect(() => {
    if (!compact) {
      setLeftOpen(false);
      setRightOpen(false);
    }
  }, [compact]);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    savePrefs({ theme });
  }, [theme]);

  // Autosave into browser storage a beat after edits stop.
  const saveTimer = useRef<number | null>(null);
  useEffect(() => {
    if (!state.dirty) return;
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      try {
        saveProject(store.pattern);
        savePrefs({ lastProjectId: store.pattern.id });
        store.markSaved();
      } catch (err) {
        store.status(
          err instanceof StorageFullError ? err.message : 'Could not autosave to this browser.',
        );
      }
    }, 1200);
    return () => {
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
    };
  }, [state.dirty, state.rev, store]);

  // Warn before losing unsaved work on a reload.
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (state.dirty) e.preventDefault();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [state.dirty]);

  const openPatternFile = useCallback(
    async (file: File) => {
      try {
        const text = await file.text();
        const { pattern, note } = parsePatternFile(text, file.name);
        store.loadPattern(pattern);
        store.status(note ? `Opened ${file.name}. ${note}` : `Opened ${file.name}`);
      } catch (err) {
        store.status((err as Error).message);
      }
    },
    [store],
  );

  const handleFile = useCallback(
    (file: File) => {
      if (file.type.startsWith('image/')) setImportFile(file);
      else openPatternFile(file);
    },
    [openPatternFile],
  );

  // Drag a file anywhere onto the window to open it.
  useEffect(() => {
    const onOver = (e: DragEvent) => {
      if (!e.dataTransfer?.types.includes('Files')) return;
      e.preventDefault();
      setDropActive(true);
    };
    const onLeave = (e: DragEvent) => {
      if (e.relatedTarget === null) setDropActive(false);
    };
    const onDrop = (e: DragEvent) => {
      e.preventDefault();
      setDropActive(false);
      const file = e.dataTransfer?.files?.[0];
      if (file) handleFile(file);
    };
    window.addEventListener('dragover', onOver);
    window.addEventListener('dragleave', onLeave);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragover', onOver);
      window.removeEventListener('dragleave', onLeave);
      window.removeEventListener('drop', onDrop);
    };
  }, [handleFile]);

  // Keyboard shortcuts.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      const mod = e.metaKey || e.ctrlKey;
      const s = store.getSnapshot();

      if (mod) {
        switch (e.key.toLowerCase()) {
          case 'z':
            e.preventDefault();
            if (e.shiftKey) store.redo();
            else store.undo();
            return;
          case 'y':
            e.preventDefault();
            store.redo();
            return;
          case 'c':
            e.preventDefault();
            store.copySelection(false);
            return;
          case 'x':
            e.preventDefault();
            store.copySelection(true);
            return;
          case 'v':
            e.preventDefault();
            store.paste();
            return;
          case 'a':
            e.preventDefault();
            store.set({ tool: 'select' });
            store.setSelection({ x: 0, y: 0, w: s.pattern.width, h: s.pattern.height });
            return;
          case 's':
            e.preventDefault();
            try {
              saveProject(store.pattern);
              savePrefs({ lastProjectId: store.pattern.id });
              store.markSaved();
              store.status('Saved to this browser');
            } catch (err) {
              store.status(err instanceof StorageFullError ? err.message : 'Save failed');
            }
            return;
          default:
            return;
        }
      }

      switch (e.key) {
        case 'b': case 'B': store.set({ tool: 'pencil' }); break;
        case 'e': case 'E': store.set({ tool: 'eraser' }); break;
        case 'l': case 'L': store.set({ tool: 'line' }); break;
        case 'r': case 'R': store.set({ tool: 'rect' }); break;
        case 'o': case 'O': store.set({ tool: 'ellipse' }); break;
        case 'g': case 'G': store.set({ tool: 'fill' }); break;
        case 'h': case 'H': store.set({ tool: 'replace' }); break;
        case 'i': case 'I': store.set({ tool: 'picker' }); break;
        case 'm': case 'M': store.set({ tool: 'select' }); break;
        case 'x': case 'X':
          store.set({ primary: s.secondary, secondary: s.primary });
          break;
        case '[':
          store.set({ brushSize: Math.max(1, s.brushSize - 1) });
          break;
        case ']':
          store.set({ brushSize: Math.min(9, s.brushSize + 1) });
          break;
        case '+': case '=':
          store.setView({ zoom: Math.min(80, s.view.zoom * 1.25) });
          break;
        case '-': case '_':
          store.setView({ zoom: Math.max(1, s.view.zoom / 1.25) });
          break;
        case 'Enter':
          if (s.floating) store.stampFloating();
          break;
        case 'Escape':
          if (s.floating) store.stampFloating();
          else store.setSelection(null);
          break;
        case 'Delete':
        case 'Backspace':
          if (s.selection) {
            e.preventDefault();
            store.deleteSelection();
          }
          break;
        default:
          if (/^[1-9]$/.test(e.key)) {
            const index = Number(e.key);
            if (index < s.pattern.palette.length) store.set({ primary: index });
          }
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [store]);

  const drawerOpen = compact && (leftOpen || rightOpen);

  return (
    <div className={`app${compact ? ' compact' : ''}${phone ? ' phone' : ''}`}>
      <TopBar
        theme={theme}
        onTheme={setTheme}
        onDialog={setDialog}
        compact={compact}
        phone={phone}
        leftOpen={leftOpen}
        rightOpen={rightOpen}
        onToggleLeft={() => {
          setLeftOpen((v) => !v);
          setRightOpen(false);
        }}
        onToggleRight={() => {
          setRightOpen((v) => !v);
          setLeftOpen(false);
        }}
        onOpenFile={async () => {
          const file = await pickFile('.json,.bead.json,.csv,application/json,text/csv');
          if (file) openPatternFile(file);
        }}
        onImportImage={async () => {
          const file = await pickFile('image/*');
          if (file) setImportFile(file);
        }}
      />
      <div className="workspace">
        <div className={`left-slot${compact ? ' drawer' : ''}${leftOpen ? ' open' : ''}`}>
          <LeftPanel />
        </div>
        <CanvasView theme={theme} />
        <div className={`right-slot${compact ? ' drawer' : ''}${rightOpen ? ' open' : ''}`}>
          <RightPanel theme={theme} />
        </div>
        {drawerOpen && (
          <div
            className="scrim"
            onPointerDown={() => {
              setLeftOpen(false);
              setRightOpen(false);
            }}
          />
        )}
      </div>
      <StatusBar />

      {dialog === 'new' && <NewPatternDialog onClose={() => setDialog(null)} />}
      {dialog === 'projects' && <ProjectsDialog onClose={() => setDialog(null)} />}
      {dialog === 'export' && <ExportDialog onClose={() => setDialog(null)} />}
      {dialog === 'shortcuts' && <ShortcutsDialog onClose={() => setDialog(null)} />}
      {dialog === 'about' && <AboutDialog onClose={() => setDialog(null)} />}
      {importFile && <ImageImportDialog file={importFile} onClose={() => setImportFile(null)} />}
      {dropActive && <div className="drop-overlay">Drop a pattern file or a picture</div>}
    </div>
  );
}

function TopBar({
  theme,
  onTheme,
  onDialog,
  onOpenFile,
  onImportImage,
  compact,
  phone,
  leftOpen,
  rightOpen,
  onToggleLeft,
  onToggleRight,
}: {
  theme: 'dark' | 'light';
  onTheme: (t: 'dark' | 'light') => void;
  onDialog: (d: DialogId) => void;
  onOpenFile: () => void;
  onImportImage: () => void;
  compact: boolean;
  phone: boolean;
  leftOpen: boolean;
  rightOpen: boolean;
  onToggleLeft: () => void;
  onToggleRight: () => void;
}) {
  const { store, state } = useEditor();
  const v = state.view;

  const nameInput = (
    <input
      className="title-input"
      value={state.pattern.name}
      aria-label="Pattern name"
      onChange={(e) =>
        store.commit((p) => {
          p.name = e.target.value;
          return p;
        })
      }
    />
  );

  if (compact) {
    const items: MenuItem[] = [
      { label: 'File', onSelect: () => {}, heading: true },
      { label: 'New pattern…', onSelect: () => onDialog('new') },
      { label: 'Saved patterns…', onSelect: () => onDialog('projects') },
      { label: 'Open file…', onSelect: onOpenFile },
      { label: 'Import picture…', onSelect: onImportImage },
      { label: 'Export…', onSelect: () => onDialog('export') },
      { label: 'View', onSelect: () => {}, heading: true },
      {
        label: `${v.mode === 'realistic' ? '✓ ' : ''}Realistic beads`,
        onSelect: () => store.setView({ mode: v.mode === 'realistic' ? 'chart' : 'realistic' }),
      },
      { label: `${v.showGrid ? '✓ ' : ''}Empty cells`, onSelect: () => store.setView({ showGrid: !v.showGrid }) },
      { label: `${v.showSymbols ? '✓ ' : ''}Symbols`, onSelect: () => store.setView({ showSymbols: !v.showSymbols }) },
      { label: `${v.showThread ? '✓ ' : ''}Thread path`, onSelect: () => store.setView({ showThread: !v.showThread }) },
      { label: `${v.showRulers ? '✓ ' : ''}Rulers`, onSelect: () => store.setView({ showRulers: !v.showRulers }) },
      { label: 'App', onSelect: () => {}, heading: true },
      { label: theme === 'dark' ? 'Light theme' : 'Dark theme', onSelect: () => onTheme(theme === 'dark' ? 'light' : 'dark') },
      { label: 'Keyboard shortcuts…', onSelect: () => onDialog('shortcuts') },
      { label: 'About…', onSelect: () => onDialog('about') },
    ];

    return (
      <header className="topbar">
        <button
          className={`btn drawer-toggle${leftOpen ? ' active' : ''}`}
          onClick={onToggleLeft}
          aria-label="Tools and colours"
        >
          ☰
        </button>
        {!phone && (
          <div className="brand">
            <span className="brand-mark">◍</span>
          </div>
        )}
        {nameInput}
        <div className="toolbar-group">
          <button className="btn" disabled={!state.canUndo} onClick={() => store.undo()} aria-label="Undo">
            ↶
          </button>
          <button className="btn" disabled={!state.canRedo} onClick={() => store.redo()} aria-label="Redo">
            ↷
          </button>
        </div>
        <Menu label="⋯" items={items} />
        <button
          className={`btn drawer-toggle${rightOpen ? ' active' : ''}`}
          onClick={onToggleRight}
          aria-label="Pattern settings"
        >
          ⚙
        </button>
      </header>
    );
  }

  return (
    <header className="topbar">
      <div className="brand">
        <span className="brand-mark">◍</span>
        <span className="brand-name">Beading Designer</span>
      </div>

      {nameInput}

      <div className="toolbar-group">
        <button className="btn" onClick={() => onDialog('new')}>New</button>
        <button className="btn" onClick={() => onDialog('projects')}>Saved</button>
        <button className="btn" onClick={onOpenFile}>Open file…</button>
        <button className="btn" onClick={onImportImage}>Import picture…</button>
        <button className="btn" onClick={() => onDialog('export')}>Export…</button>
      </div>

      <div className="toolbar-group">
        <button className="btn" disabled={!state.canUndo} onClick={() => store.undo()} title="Undo (⌘Z)">
          ↶
        </button>
        <button className="btn" disabled={!state.canRedo} onClick={() => store.redo()} title="Redo (⇧⌘Z)">
          ↷
        </button>
        <button
          className="btn"
          disabled={!state.selection}
          onClick={() => store.transformSelection('flipH')}
          title="Flip the selection left/right"
        >
          ⇋
        </button>
        <button
          className="btn"
          disabled={!state.selection}
          onClick={() => store.transformSelection('flipV')}
          title="Flip the selection top/bottom"
        >
          ⇵
        </button>
      </div>

      <div className="toolbar-group">
        <button
          className={`btn${v.mode === 'realistic' ? ' active' : ''}`}
          onClick={() => store.setView({ mode: v.mode === 'realistic' ? 'chart' : 'realistic' })}
          title="Shaded bead preview"
        >
          Beads
        </button>
        <button
          className={`btn${v.showGrid ? ' active' : ''}`}
          onClick={() => store.setView({ showGrid: !v.showGrid })}
          title="Show empty cells"
        >
          Grid
        </button>
        <button
          className={`btn${v.showSymbols ? ' active' : ''}`}
          onClick={() => store.setView({ showSymbols: !v.showSymbols })}
          title="Symbols for mono printing"
        >
          Sym
        </button>
        <button
          className={`btn${v.showThread ? ' active' : ''}`}
          onClick={() => store.setView({ showThread: !v.showThread })}
          title="Show the thread path"
        >
          Thread
        </button>
        <button
          className={`btn${v.showRulers ? ' active' : ''}`}
          onClick={() => store.setView({ showRulers: !v.showRulers })}
          title="Row and column numbers"
        >
          Rulers
        </button>
      </div>

      <div className="toolbar-group right">
        <button className="btn" onClick={() => onTheme(theme === 'dark' ? 'light' : 'dark')} title="Light / dark">
          {theme === 'dark' ? '☀' : '☾'}
        </button>
        <button className="btn" onClick={() => onDialog('shortcuts')} title="Keyboard shortcuts">⌨</button>
        <button className="btn" onClick={() => onDialog('about')} title="About">?</button>
      </div>
    </header>
  );
}

function StatusBar() {
  const { state } = useEditor();
  const p = state.pattern;
  const primary = p.palette[state.primary];
  const secondary = p.palette[state.secondary];

  return (
    <footer className="statusbar">
      <span className="status-msg">{state.status || 'Ready'}</span>
      <span className="spacer" />
      <span className="dim">
        {state.dirty ? 'Unsaved changes' : 'Saved'} · {p.width}×{p.height}
      </span>
      <span className="ink-pair" title="Left-click colour / right-click colour">
        <span
          className="ink"
          style={{ background: state.primary === EMPTY ? 'transparent' : primary?.hex ?? 'transparent' }}
        />
        <span
          className="ink small"
          style={{ background: state.secondary === EMPTY ? 'transparent' : secondary?.hex ?? 'transparent' }}
        />
      </span>
    </footer>
  );
}
